import { useTranslations } from 'next-intl';

import CoinIcon from '@/assets/icons/coin.svg';
import USDCIcon from '@/assets/icons/usdc.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Link } from '@/i18n/navigation';
import { formatNumberWithCommas } from '@/lib/utils/formatNumberWithCommas';
import { T_Pool } from '@/ts/types/T_Pool';

interface I_PoolsTokenInfoProps {
  token: T_Pool;
  closeModal?: () => void;
  isModal?: boolean;
}

const parseFiniteNumber = (value: unknown) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

function PoolsTokenInfo({ token, closeModal, isModal }: I_PoolsTokenInfoProps) {
  const t = useTranslations('pools');

  const totalSupply = parseFiniteNumber(token.totalSupply);

  // Some API responses may include `tokenSupply` (pool allocation).
  const tokenSupplyRaw = (token as unknown as { tokenSupply?: string | number })
    .tokenSupply;
  const poolTokens = parseFiniteNumber(tokenSupplyRaw ?? token.totalSupply);

  const soldTokens = parseFiniteNumber(
    token?.blockchainData?.totalSoldTokensDecimal || '0'
  );

  const availableTokens = Math.max(0, totalSupply - soldTokens);
  const availablePercent =
    totalSupply > 0 ? (availableTokens / totalSupply) * 100 : 0;
  const availablePercentClamped = Math.min(100, Math.max(0, availablePercent));

  return (
    <div className="p-7 flex flex-col gap-8 gradien bg-(image:--card-gradient) rounded-2xl">
      <div className="w-full flex justify-between gap-5">
        <div className="w-33">
          <P className="font-medium">{t('poolVolume')}</P>
          <P
            className="font-bold"
            icon={<USDCIcon className="text-foreground w-5 h-5" />}
          >
            {formatNumberWithCommas(
              token?.blockchainData?.currentUsdcBalanceDecimal || '0',
              2
            )}
          </P>
        </div>
        <span className="border-r border-" />
        <div className="w-33">
          <P className="font-medium">{t('poolTokens')}</P>
          <P
            className="font-bold"
            icon={<CoinIcon className="text-foreground w-5 h-5" />}
          >
            {formatNumberWithCommas(poolTokens)}
          </P>
        </div>
      </div>
      <div className="w-full flex flex-col gap-2.5">
        <Progress value={availablePercentClamped} className="h-4" />
        <div className="flex justify-between items-center">
          <P className="text-sm font-medium">{t('availableTokens')}</P>
          <P className="text-sm font-medium">
            {formatNumberWithCommas(availableTokens)} (
            {Math.round(availablePercentClamped)}%)
          </P>
        </div>
      </div>
      <Button variant="brand" className="text-xl h-fit leading-5" asChild>
        <Link
          href={`/pools/${token.slug}`}
          onClick={() => {
            if (isModal && closeModal) {
              closeModal();
            }
          }}
        >
          {t('buyTokens')}
        </Link>
      </Button>
    </div>
  );
}

export default PoolsTokenInfo;
